import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { promptApi, insightsApi } from '../services/api';
import { InsightsData } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { usePortalSlot } from '../hooks/usePortalSlot';
import { useSelectedBlueprint } from '../hooks/useSelectedBlueprint';
import AnalysisProfileSelect from './AnalysisProfileSelect';
import EChart from './charts/EChart';
import { useChartPalette } from './charts/useChartMode';
import { ChartPalette, matchOutcomeColors } from './charts/chartTheme';
import { donutOption, stackedBarOption, withAlpha } from './charts/options';
import { StatCard } from './ui/StatCard';

const Insights: React.FC = () => {
  const { user } = useAuth();
  const { selectedCompanyId } = useCompany();
  const palette = useChartPalette();
  // Matched teal / Unmatched pink — one source for the donut, its legend rows
  // and the trend bars.
  const outcomeColors = matchOutcomeColors(palette);
  const [insights, setInsights] = useState<InsightsData | null>(null);
  // The selected blueprint's own name, fetched by id (deepcontext-q2bz.2) —
  // not looked up in a client-side list, so a blueprint the getAll preload
  // never fetched (created after the cache filled) still resolves.
  const [selectedPromptName, setSelectedPromptName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storedBlueprintId, setStoredBlueprintId, clearStoredBlueprint] = useSelectedBlueprint();
  // Seed the selection from the persisted blueprint id on mount so the choice
  // survives navigation/reloads and is shared with the other blueprint-aware
  // pages.
  const [selectedPromptId, setSelectedPromptId] = useState<string>(() => storedBlueprintId);
  const [trendDays, setTrendDays] = useState<7 | 30>(7);

  const lastLoadedKey = useRef<string | null>(null);

  // deepcontext-6i0j.4 — the blueprint selector used
  // to render in the page body (InsightsHeader), pushing the stat tiles down
  // a line relative to every other page. It now portals into AppLayout's
  // left context slot (deepcontext-tpe1), captioned and first on the row.
  const contextSlot = usePortalSlot('app-page-context');

  // Persist every selection change through to localStorage while keeping the
  // local state authoritative for this page's data fetching.
  const handleSelectBlueprint = useCallback((id: string) => {
    setSelectedPromptId(id);
    setStoredBlueprintId(id);
  }, [setStoredBlueprintId]);

  // When the persisted blueprint no longer resolves (deleted/inaccessible),
  // clear storage and reset to the empty "Select a brand blueprint" state.
  const handleInvalidSelection = useCallback(() => {
    clearStoredBlueprint();
    setSelectedPromptId('');
  }, [clearStoredBlueprint]);

  useEffect(() => {
    if (!selectedPromptId) {
      setInsights(null);
      setError(null);
      return;
    }
    const loadKey = `${selectedPromptId}-${selectedCompanyId}-${trendDays}`;
    if (loadKey === lastLoadedKey.current) return;
    loadInsights(loadKey);
  }, [selectedPromptId, selectedCompanyId, trendDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the selected blueprint's name by id (deepcontext-q2bz.2) instead
  // of a client-side getAll preload — the picker itself searches server-side
  // (AnalysisProfileSelect), so this is the only prompt lookup Insights
  // still needs.
  useEffect(() => {
    if (!selectedPromptId) {
      setSelectedPromptName('');
      return;
    }
    let cancelled = false;
    const companyIdToUse = (user?.role === 'eskimi_admin' || user?.role === 'adops') ? selectedCompanyId || undefined : undefined;
    promptApi.getById(selectedPromptId, companyIdToUse)
      .then(prompt => {
        if (!cancelled) setSelectedPromptName(prompt?.name || '');
      })
      .catch(err => {
        console.error('Failed to load blueprint name:', err);
        if (!cancelled) setSelectedPromptName('');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPromptId, selectedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInsights = async (loadKey?: string) => {
    const key = loadKey || `${selectedPromptId}-${selectedCompanyId}-${trendDays}`;
    lastLoadedKey.current = key;
    try {
      setIsLoading(true);
      const companyIdToUse = (user?.role === 'eskimi_admin' || user?.role === 'adops') ? selectedCompanyId || undefined : undefined;
      const insightsData = await insightsApi.getForPrompt(selectedPromptId, companyIdToUse, trendDays);
      setInsights(insightsData);
    } catch (err) {
      console.error('Failed to load insights:', err);
      setError('Failed to load insights data');
      lastLoadedKey.current = null;
    } finally {
      setIsLoading(false);
    }
  };

  const retryLoadInsights = async () => {
    if (!selectedPromptId) return;
    lastLoadedKey.current = null;
    await loadInsights();
  };

  const filteredInsights = insights;

  const getAverageConfidence = () => {
    if (!filteredInsights || filteredInsights.byPrompt.length === 0) return 0;
    return filteredInsights.byPrompt[0]?.avgConfidence || 0;
  };
  
  const getTotalMatchingUrls = () => {
    if (!filteredInsights) return 0;
    return filteredInsights.byPrompt.reduce((sum, p) => sum + p.matchedUrls, 0);
  };

  // Memoized (and kept above the early returns, where hooks have to live): a
  // fresh array identity every render would rebuild the whole trend chart via
  // EChart's notMerge setOption on each parent render.
  const trendData = useMemo(
    () => (insights?.recentTrends || []).map(t => ({
      date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      matched: t.matchedUrls,
      unmatched: t.unmatchedUrls,
      total: t.totalUrls,
      matchRate: Math.round(t.matchRate * 100)
    })),
    [insights?.recentTrends],
  );

  // Portaled into AppLayout's left context slot (see contextSlot above) so
  // it renders once per branch below without repeating the props each time.
  const headerActions = contextSlot && createPortal(
    <InsightsHeaderActions
      selectedPromptId={selectedPromptId}
      setSelectedPromptId={handleSelectBlueprint}
      onInvalidSelection={handleInvalidSelection}
    />,
    contextSlot,
  );

  if (!selectedPromptId) {
    return (
      <div className="space-y-8">
        {headerActions}
        <div className="bg-surface rounded-xl shadow-sm border border-line p-12 text-center">
          <p className="text-ink-muted">Select a brand blueprint above to view analysis overview.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        {headerActions}
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  if (error || !insights) {
    return (
      <div className="space-y-8">
        {headerActions}
        <div className="rounded-xl p-4 bg-risk-bg border border-risk">
          <p className="text-risk">{error || 'No insights data available'}</p>
          <button onClick={() => retryLoadInsights()} className="mt-2 underline text-sm text-risk">Try Again</button>
        </div>
      </div>
    );
  }

  if ((filteredInsights?.totalAnalyses || 0) === 0) {
    return (
      <div className="space-y-8">
        {headerActions}
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4 text-ink">No Data Available</h2>
          <p className="text-ink-muted">
            No analysis data available for the selected blueprint: {selectedPromptName}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {headerActions}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard label="Total URLs Analyzed" value={String(filteredInsights?.totalAnalyses || 0)} />
        <StatCard
          label="Match Rate"
          value={`${Math.round((filteredInsights?.overallMatchRate || 0) * 100)}%`}
          valueColorClassName="text-safe"
        />
        <StatCard label="Total Matching URLs" value={String(getTotalMatchingUrls())} valueColorClassName="text-accent" />
        <StatCard
          label="Average Confidence (Matched)"
          value={`${Math.round(getAverageConfidence() * 100)}%`}
          valueColor={palette.categorical[2]}
        />
      </div>

      {/* Donut + Trends share one row — the donut never sits beside an empty column. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
        <div className="bg-surface rounded-xl shadow-sm border border-line p-6 flex flex-col">
          <h3 className="text-sm font-bold mb-3 text-ink flex items-center gap-2">
            Results Distribution
            <span className="text-xs font-semibold text-ink-muted">
              {selectedPromptName}
            </span>
          </h3>
          <div className="flex items-center gap-5 flex-1">
            <ResultsDonut
              palette={palette}
              matched={getTotalMatchingUrls()}
              unmatched={(filteredInsights?.totalAnalyses || 0) - getTotalMatchingUrls()}
              matchRate={filteredInsights?.overallMatchRate || 0}
            />
            <div className="flex-1 flex flex-col gap-2 min-w-0">
              <DonutLegendRow
                hue={outcomeColors.matched}
                name="Matched"
                count={getTotalMatchingUrls()}
                share={filteredInsights?.overallMatchRate || 0}
              />
              <DonutLegendRow
                hue={outcomeColors.unmatched}
                name="Unmatched"
                count={(filteredInsights?.totalAnalyses || 0) - getTotalMatchingUrls()}
                share={1 - (filteredInsights?.overallMatchRate || 0)}
              />
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl shadow-sm border border-line p-6 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-ink">Analysis Trends</h3>
            <TrendRangeSwitch value={trendDays} onChange={setTrendDays} />
          </div>
          <TrendBars palette={palette} data={trendData} />
        </div>
      </div>
    </div>
  );
};

function TrendRangeSwitch({ value, onChange }: { value: 7 | 30; onChange: (d: 7 | 30) => void }) {
  return (
    <div className="flex items-center gap-1 rounded-full bg-ghost-bg p-1">
      {([7, 30] as const).map(d => (
        <button
          key={d}
          onClick={() => onChange(d)}
          className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${
            value === d ? 'bg-surface text-accent-active shadow-sm' : 'text-ink-muted'
          }`}
        >
          {d}D
        </button>
      ))}
    </div>
  );
}

// A legend row is tinted with a soft wash of the slice it stands for, so the
// row, its dot and its name always match the donut.
const LEGEND_ROW_TINT_ALPHA = 0.1;

// Tinted stat row for the donut's legend — hue-washed background, name in the
// slice hue, right-aligned count and share (mockup's `.dstat` pattern).
function DonutLegendRow({
  hue,
  name,
  count,
  share,
}: {
  hue: string;
  name: string;
  count: number;
  share: number;
}) {
  return (
    <div
      data-testid="donut-legend-row"
      className="flex items-center gap-2.5 rounded-xl px-3 py-2.5"
      style={{ backgroundColor: withAlpha(hue, LEGEND_ROW_TINT_ALPHA) }}
    >
      <span
        data-testid="donut-legend-dot"
        className="h-2.5 w-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: hue }}
      />
      <span className="text-xs font-bold" style={{ color: hue }}>{name}</span>
      <span className="ml-auto text-sm font-extrabold text-ink">{count.toLocaleString()}</span>
      <span className="text-xs font-bold text-ink-muted w-10 text-right">{Math.round(share * 100)}%</span>
    </div>
  );
}

// Matched vs unmatched is the page's headline verdict: a padded donut in the
// teal/pink categorical pair with the match rate as the centre KPI.
function ResultsDonut({
  palette,
  matched,
  unmatched,
  matchRate,
}: {
  palette: ChartPalette;
  matched: number;
  unmatched: number;
  matchRate: number;
}) {
  const option = useMemo(
    () =>
      donutOption({
        palette,
        slices: [
          { name: 'Matched', value: matched, color: matchOutcomeColors(palette).matched },
          { name: 'Unmatched', value: unmatched, color: matchOutcomeColors(palette).unmatched },
        ],
        centerValue: `${Math.round(matchRate * 100)}%`,
        centerLabel: 'MATCH RATE',
        valueFormatter: (value) => `${value.toLocaleString()} URLs`,
        // The legend renders as tinted stat rows in JSX (DonutLegendRow)
        // instead of ECharts' own legend.
        showLegend: false,
        track: true,
        height: 150,
      }),
    [palette, matched, unmatched, matchRate],
  );

  return (
    <div className="w-[150px] h-[150px] flex-shrink-0">
      <EChart option={option} height={150} ariaLabel="Matched versus unmatched URLs" />
    </div>
  );
}

function TrendBars({
  palette,
  data,
}: {
  palette: ChartPalette;
  data: Array<{ date: string; matched: number; unmatched: number }>;
}) {
  const option = useMemo(
    () =>
      stackedBarOption({
        palette,
        categories: data.map(d => d.date),
        series: [
          { name: 'Matched', data: data.map(d => d.matched), color: matchOutcomeColors(palette).matched },
          { name: 'Unmatched', data: data.map(d => d.unmatched), color: matchOutcomeColors(palette).unmatched },
        ],
      }),
    [palette, data],
  );

  return <EChart option={option} height={300} ariaLabel="Matched and unmatched URLs per day" />;
}

// deepcontext-6i0j.4 — blueprint selector, portaled
// into AppLayout's left context slot (see contextSlot in Insights),
// captioned and first on the row (deepcontext-tpe1).
function InsightsHeaderActions({
  selectedPromptId,
  setSelectedPromptId,
  onInvalidSelection,
}: {
  selectedPromptId: string;
  setSelectedPromptId: (id: string) => void;
  onInvalidSelection: () => void;
}) {
  return (
    <>
      <div className="w-56">
        <AnalysisProfileSelect
          value={selectedPromptId}
          onChange={(value) => setSelectedPromptId(value || '')}
          onInvalidSelection={onInvalidSelection}
          placeholder="Select a Brand Blueprint"
          className="w-full"
          captioned
        />
      </div>
    </>
  );
}

export default Insights;